import { ChunkSizeControl } from "@/components/ChunkSizeControl";
import { CodeBlock } from "@/components/CodeBlock";
import { Field, FieldGroup } from "@/components/HashDisplay";
import { Prop } from "@/components/Prop";
import { toHex } from "@/hashtree/bytes";
import type { HashtreeBuild } from "@/hashtree/useHashtreeBuild";
import { Slide, SpecNote } from "./Slide";

export function ChunkSizeSlide({ build, onNext }: { build: HashtreeBuild; onNext: () => void }) {
  const { chunkSize, setChunkSize, maxLinks, setMaxLinks, geometry, tree, stale, building } = build;

  return (
    <Slide
      bud={17}
      title="Choose how the file splits"
      lede={
        <>
          <code>chunk_size</code> and <code>max_links</code> are chosen by whoever writes the tree. A reader never looks
          at them — it just follows each link's <Prop name="h" /> and <Prop name="s" /> and concatenates. Move the slider and
          watch the shape, and the root hash, change.
        </>
      }
    >
      <ChunkSizeControl
        chunkSize={chunkSize}
        onChunkSize={setChunkSize}
        maxLinks={maxLinks}
        onMaxLinks={setMaxLinks}
        geometry={geometry}
      />

      {tree && (
        <FieldGroup>
          <Field
            label="root hash"
            value={toHex(tree.root.encrypted.blobHash)}
            tone="hash"
            hint={
              stale || building
                ? "rebuilding…"
                : "The whole tree rehashes when you move the slider — this is the value that changes."
            }
          />
          <Field label="blobs" value={`${tree.blobs.length}`} copyable={false} />
        </FieldGroup>
      )}

      <CodeBlock
        caption="BUD-17 splitting, verbatim from the spec"
        code={`1. If the file is at most chunk_size bytes, publish it as a single blob.
2. Split larger files into consecutive chunks of at most chunk_size bytes.
3. Create a t=0 link for each chunk with h, s, and optional k.
4. If a node has more than max_links links, group consecutive links
   into child t=1 manifests of at most max_links links.
5. Parent links use t=1 and s = sum of descendant plaintext sizes.
6. Repeat until the root has at most max_links links.`}
      />

      <SpecNote title="Why this does not break interoperability">
        Nothing in the reading rules mentions a chunk size or a link limit. A tree chunked at 1 KiB resolves in any
        conformant client exactly like one chunked at 2 MiB. The cost of a non-canonical size is dedup: another client
        uploading the same file at 2 MiB produces a different root hash, so the two copies will not share blobs.
      </SpecNote>

      <button
        type="button"
        onClick={onNext}
        disabled={!tree}
        className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-opacity hover:opacity-90 disabled:opacity-50"
      >
        Encrypt the chunks
      </button>
    </Slide>
  );
}
